import dotProp from 'dot-prop-immutable'
import findIndex from 'lodash/findIndex'
import deleteRecord from '@/utils/delete-record'
import updateRecord from '@/utils/update-record'
import { isESRContract, isLoan } from '@/utils/contract-utils'

// API resources
import apiAccounts from '@/api/modules/accounts'
import apiPortfolios from '@/api/modules/portfolios'
import apiUser from '@/api/modules/user'

// Mutations
import {
  ADD_ACCOUNTS_LIST,
  ADD_ACCOUNT,
  DELETE_ACCOUNT,
  ADD_ACCOUNTS_REPARTITION,
  ADD_ACCOUNTS_REPARTITION_ESR,
  DELETE_ACCOUNTS_REPARTITION,
  UPDATE_ACCOUNTS_LIST,
  UPDATE_ACCOUNT_PORTFOLIO,
  RESET_MODULE
} from '@/store/mutation-types'

const filterPortfolios = (plist, criteria) => {
  const list = JSON.parse(JSON.stringify(plist))
  if (criteria === 'holdings') {
    return list.filter(item => {
      return !isLoan(item) && !isESRContract(item)
    })
  } else if (criteria === 'loans') {
    return list.filter(item => { return isLoan(item) })
  } else if (criteria === 'devices') {
    return list.filter(item => { return isESRContract(item) })
  } else {
    return list
  }
}

// Default state
const getDefaultState = () => {
  return {
    list: [],
    originalList: [],
    waiting: {},
    accountsRepartition: [],
    accountsRepartitionESR: []
  }
}
const state = getDefaultState()

// -----------------------------------------------------------------------------

const actions = {
  add (context, data) {
    return apiUser.addAccount(data)
      .catch((err) => {
        return Promise.reject(err)
      })
  },

  delete (context, id) {
    return apiAccounts.delete(id)
      .then(() => {
        context.dispatch('fetchRepartition')
        context.dispatch('contracts/fetchRepartition', null, { root: true })
        return context.commit(DELETE_ACCOUNT, id)
      })
  },

  fetch (context, options = {}) {
    const id = options.id || undefined
    const cached = options.cached || false
    const { list } = context.state

    if (cached && list.length) {
      return Promise.resolve(list)
    }

    return apiUser.fetchAccounts(id)
      .then(data => {
        context.commit(ADD_ACCOUNTS_LIST, data)
      })
  },

  fetchById ({ state, commit }, { id, cached = false }) {
    const { list } = state
    const cachedAccount = list.find(account => parseInt(account.id) === parseInt(id))

    if (cached && cachedAccount) {
      return Promise.resolve()
    }

    return apiUser.fetchAccount(id)
      .then(data => {
        commit(ADD_ACCOUNT, data)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  },

  fetchRepartition (context, cache) {
    if (cache && context.state.accountsRepartition.length) {
      return Promise.resolve()
    }
    return apiUser.fetchAccountsRepartition()
      .then(({ data }) => context.commit(ADD_ACCOUNTS_REPARTITION, data))
  },

  fetchRepartitionESR (context, cache) {
    if (cache && context.state.accountsRepartitionESR.length) {
      return Promise.resolve()
    }
    return apiUser.fetchAccountsRepartitionESR()
      .then(({ data }) => context.commit(ADD_ACCOUNTS_REPARTITION_ESR, data))
  },

  synchronize ({ commit }, accountId) {
    return apiAccounts.synchronize(accountId)
      .then(data => {
        commit(UPDATE_ACCOUNTS_LIST, data)
        return Promise.resolve()
      })
  },

  // PUTs new portfolio data on the server then commits in store
  updatePortfolio (context, { portfolioId, data, update = true }) {
    return apiPortfolios.updatePortfolio(portfolioId, data)
      .then((response) => {
        context.commit(UPDATE_ACCOUNT_PORTFOLIO, {
          accountId: response.accountId,
          portfolioId,
          data
        })
      })
  },

  resetRepartition (context) {
    context.commit(DELETE_ACCOUNTS_REPARTITION)
  },

  resetState (context) {
    context.commit(RESET_MODULE)
  }
}

const mutations = {
  [ADD_ACCOUNTS_LIST] (state, data) {
    state.originalList = data
    state.list = data
    this.commit('accounts/filterList', this.state.ux.sidebarSection)
  },
  filterList(state, criteria) {
    const list = JSON.parse(JSON.stringify(state.originalList))
    const filteredList = list.filter(item => {
      const filteredPortfolios = filterPortfolios(item.portfolios, criteria)
      item.portfolios = filteredPortfolios.length > 0
        ? filteredPortfolios
        : item.portfolios
      return filteredPortfolios.length > 0
    })
    state.list = filteredList
  },

  [ADD_ACCOUNT] (state, data) {
    const index = findIndex(state.list, { id: data.id })
    if (index >= 0) {
      state.list = [
        ...state.list.slice(0, index),
        data,
        ...state.list.slice(index + 1)
      ]
    } else {
      state.list = [
        ...state.list,
        data
      ]
    }
  },

  [ADD_ACCOUNTS_REPARTITION] (state, data) {
    state.accountsRepartition = data
  },

  [ADD_ACCOUNTS_REPARTITION_ESR] (state, data) {
    state.accountsRepartitionESR = data
  },

  [DELETE_ACCOUNTS_REPARTITION] (state) {
    state.accountsRepartition = []
  },

  [DELETE_ACCOUNT] ({ list }, id) {
    deleteRecord(list, id)
  },

  [UPDATE_ACCOUNTS_LIST] ({ list }, data) {
    updateRecord(list, data, false)
  },

  [UPDATE_ACCOUNT_PORTFOLIO] (state, { accountId, portfolioId, data }) {
    const accountIndex = findIndex(state.list, { id: accountId })
    if (accountIndex > -1) {
      const portfolioIndex = findIndex(state.list[accountIndex].portfolios, { id: portfolioId })
      if (portfolioIndex > -1) {
        state.list = dotProp.merge(state.list, `${accountIndex}.portfolios.${portfolioIndex}`, data)
      }
    }
  },

  [RESET_MODULE] (state) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  getById ({ list }) {
    return id => list.find(account => account.id === id)
  },

  getByConnexionId ({ list }) {
    return connexionId => list.find(account => account.connexionId === connexionId)
  },

  getPortfoliosByKind: ({ originalList }) => (kind) => {
    const portfolios = []
    for (const account of originalList) {
      portfolios.push(...(account.portfolios.filter(portfolio => portfolio.kind === kind && portfolio.status !== 'hidden')))
    }
    return portfolios
  },

  hasAccountErrors ({ list }) {
    return list.some(a => {
      return a.state === 'error'
    })
  },

  getAccountByPortfolioId: ({ list }) => (portfolioId) => {
    return list.find(account => account.portfolios.some((portfolio) => parseInt(portfolio.id) === parseInt(portfolioId)))
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
